/**
 * toastFields — shared helpers for Pedro's 2026-06-10 toast convention:
 * Save toasts name the changed fields, capped at three + "+N more" so a
 * bulk edit can never produce an enormous toast. Creates are unaffected
 * (they keep their "created" toasts); the audit log remains the full
 * inventory of every change.
 *
 * Reference implementation: AgreementDetailPage (#21 polish). Ported
 * app-wide via task #22.
 */
import { sameValue } from './diffPayload';

/** Render the capped "(A, B, C +N more)" suffix, or '' when no fields. */
export function fieldSuffix(labels?: readonly string[]): string {
  if (!labels || labels.length === 0) return '';
  const shown = labels.slice(0, 3).join(', ');
  const extra = labels.length - 3;
  return ` (${shown}${extra > 0 ? ` +${extra} more` : ''})`;
}

/** Acronyms / initialisms that the generic humanizer should uppercase. */
const ACRONYMS = new Set([
  'id', 'kyc', 'aml', 'nif', 'tin', 'lei', 'nipc', 'iban', 'bic', 'swift',
  'mifid', 'mifir', 'pep', 'rto', 'us', 'nci', 'pcc', 'rcbe', 'iaa', 'saa',
]);

/** "kyc_last_date" → "KYC last date"; "bank_name" → "Bank name". */
export function labelizeField(key: string): string {
  const words = key.split('_').map((w) =>
    ACRONYMS.has(w) ? w.toUpperCase() : w,
  );
  const joined = words.join(' ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/**
 * Friendly labels for every field whose value differs between the form's
 * current values and its pre-save baseline. `overrides` wins over the
 * generic humanizer for fields whose nice name isn't derivable
 * (e.g. mifir_id → "MIFIR ID", swift_bic → "SWIFT/BIC").
 */
export function changedFieldLabels<T extends object>(
  values: T,
  initial: T,
  overrides?: Record<string, string>,
): string[] {
  const v = values as Record<string, unknown>;
  const i = initial as Record<string, unknown>;
  return Object.keys(v)
    .filter((k) => !sameValue(v[k], i[k]))
    .map((k) => overrides?.[k] ?? labelizeField(k));
}
